"use client";

import ArrowLeftIcon from "@/app/_components/Icons/ArrowLeftIcon";
import { constants } from "@/environment/constants";
import { Box, Card, CardActionArea, Icon, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import Image from "next/image";
import NextLink from "next/link";

const HREF = "/products?sort=sale&utm_source=zibasi&utm_medium=banner";

const shimmer = keyframes`
  0% { background-position: 120% 0; }
  100% { background-position: -120% 0; }
`;

const floatOrb = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-10px, 8px) scale(1.06); }
`;

const ctaPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(212, 129, 54, 0.4); }
  50% { box-shadow: 0 0 0 10px rgba(212, 129, 54, 0); }
`;

/**
 * Desktop-only hero. Wheel overlaps bottom-start (~200px).
 * Portrait is a transparent cutout — contain keeps subject edge-to-edge
 * top→bottom (cover would crop head/arms on wide boxes).
 */
export default function HeroBentoCard() {
  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 5,
        overflow: "hidden",
        position: "relative",
        isolation: "isolate",
        background: "linear-gradient(125deg, #F8F5F1 0%, #F3EBE3 42%, #FFE8D4 78%, #FFD9B8 100%)",
      }}
    >
      <CardActionArea
        component={NextLink}
        href={HREF}
        aria-label={`مشاهده محصولات ${constants.siteName}`}
        sx={{
          width: "100%",
          height: "100%",
          display: "block",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            insetInlineStart: "18%",
            top: -80,
            width: 280,
            height: 280,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(231, 166, 107, 0.22) 0%, rgba(231, 166, 107, 0) 70%)",
            animation: `${floatOrb} 9s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.18) 50%, transparent 62%)",
            backgroundSize: "220% 100%",
            animation: `${shimmer} 7s ease-in-out infinite`,
            pointerEvents: "none",
            zIndex: 1,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background:
              "linear-gradient(to right, rgba(248, 245, 241, 0.92) 0%, rgba(248, 245, 241, 0.72) 22%, rgba(248, 245, 241, 0.15) 38%, transparent 48%)",
          }}
        />

        {/* Portrait — full height, contain, never clipped */}
        <Box
          sx={{
            position: "absolute",
            insetInlineEnd: 0,
            top: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: "none",
            height: "100%",
            aspectRatio: "683 / 500",
            filter: "drop-shadow(0 18px 36px rgba(60, 35, 15, 0.14))",
          }}
        >
          <Image
            src="/main/2.webp"
            alt={constants.siteName}
            fill
            priority
            draggable={false}
            sizes="700px"
            style={{
              objectFit: "contain",
              objectPosition: "center center",
              userSelect: "none",
            }}
          />
        </Box>

        <Stack
          sx={{
            position: "absolute",
            zIndex: 2,
            insetInlineStart: 0,
            top: 0,
            bottom: { md: 200, lg: 208 },
            width: { md: "44%", lg: "42%" },
            px: { md: 4.5, lg: 5.5 },
            pt: { md: 4, lg: 4.5 },
            pb: { md: 2, lg: 2.5 },
            boxSizing: "border-box",
            justifyContent: "flex-start",
            gap: { md: 1.5, lg: 1.75 },
          }}
        >
          <Box>
            <Typography
              component="div"
              sx={{
                fontWeight: 900,
                fontSize: { md: "2.5rem", lg: "3rem" },
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                color: "grey.900",
              }}
            >
              {constants.siteName}
            </Typography>
            <Box
              aria-hidden
              sx={{
                mt: 1,
                width: 48,
                height: 3,
                borderRadius: 2,
                background: "linear-gradient(90deg, #E88136 0%, #F0B27A 100%)",
              }}
            />
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { md: "1.25rem", lg: "1.4rem" },
              lineHeight: 1.45,
              color: "grey.800",
              maxWidth: 360,
            }}
          >
            کیفیت و زیبایی،
            <br />
            با سلیقه‌ی خودت
          </Typography>

          <Typography
            sx={{
              color: "grey.700",
              maxWidth: 340,
              lineHeight: 1.6,
              fontSize: { md: "0.95rem", lg: "1.02rem" },
              fontWeight: 400,
            }}
          >
            جایی که هر روز می‌تونی خاص‌تر از دیروز بدرخشی.
          </Typography>

          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.75 }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2.25,
                py: 1.05,
                borderRadius: 300,
                bgcolor: "secondary.main",
                color: "#fff",
                fontWeight: 800,
                animation: `${ctaPulse} 2.6s ease-in-out infinite`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            >
              <Typography variant="body1" sx={{ color: "inherit", fontWeight: 800 }}>
                شروع خرید
              </Typography>
              <Icon sx={{ color: "inherit" }}>
                <ArrowLeftIcon />
              </Icon>
            </Box>
            <Typography variant="body2" sx={{ color: "grey.600", fontWeight: 600 }}>
              ارسال سریع · تضمین اصالت
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
